import cn from "classnames"
import { lazy, Suspense } from "react"

import { Skeleton } from "@/atoms"
import {
  FIXABLE_DATA_ID_ANALYTIC_FUNNEL_CARD,
  RUNTIME_ID
} from "@/const/default-values"
import { preventDefault } from "@/helpers/utils"
import { useNumberConvertor } from "@/hooks/useNumberConvertor"
import { useFeatureFlags } from "@/hooks/useUser"
import { ToolOutlined } from "@/icons"
import { StatisticCard } from "@/pages/DashboardPage/components/molecules/StatisticCard"
import { addFixableToLink } from "@/pages/DashboardPage/hooks/helpers"

import styles from "./FunnelCard.module.scss"

import type { FunnelCardComponent } from "."
import { RuntimeLowCoverageTeaser } from "./RuntimeLowCoverageTeaser"

const RuntimeTeasersFunnelCard = lazy(
  () => import("./RuntimeTeasersFunnelCard")
)

export const FunnelCard: FunnelCardComponent = (props) => {
  const {
    d,
    maxViewBox,
    title,
    Icon,
    color,
    loading,
    to,
    selected,
    size,
    fixable,
    isLowRuntimeCoverage,
    runtimeCount,
    navigate,
    isDemo,
    ...rest
  } = props

  const useGradient = Array.isArray(color)
  const gradientId = `${props.id}_gradient`
  const fill = useGradient ? `url(#${gradientId})` : color
  const isSmall = props.size === "sm"
  const commonClasses = cn({
    [styles.small]: isSmall,
    [styles.loading]: loading
  })
  const kortexInstalled = useFeatureFlags("kortexInstalled")
  const isRuntimeCard = rest.id === RUNTIME_ID
  const showDeployRuntimeTeaser = !kortexInstalled && isRuntimeCard
  const showLowRuntimeTeaser = isLowRuntimeCoverage && isRuntimeCard
  const isBlur = showLowRuntimeTeaser || showDeployRuntimeTeaser
  const classes = {
    container: cn(styles.container, commonClasses, isBlur && styles.blur),
    children: cn(styles.children, commonClasses)
  }
  const { abbreviateNumber } = useNumberConvertor()

  if (loading) {
    const { flex, size, span } = props

    return (
      <StatisticCard
        flex={flex}
        span={span}
        size={size}
        classes={classes}
        valueRender={() => null}
      >
        <Skeleton.Node active />
      </StatisticCard>
    )
  }

  const isFixable = fixable !== undefined
  const showFixableV1 = isFixable && isDemo
  const showFixable = isFixable && !showFixableV1

  return (
    <StatisticCard
      classes={classes}
      footer={
        <>
          {showLowRuntimeTeaser && (
            <RuntimeLowCoverageTeaser
              to={to}
              isSmall={isSmall}
              runtimeCount={runtimeCount}
            />
          )}
          {showDeployRuntimeTeaser && (
            <Suspense>
              <RuntimeTeasersFunnelCard isSmall={isSmall} />
            </Suspense>
          )}
        </>
      }
      title={
        <span
          className={cn(
            styles.title,
            isDemo && styles.titleV1,
            isBlur && styles.hidden
          )}
        >
          <Icon className={styles.icon} />
          <span className={styles.titleText}>{title}</span>
        </span>
      }
      to={isBlur ? undefined : to}
      selected={showDeployRuntimeTeaser ? false : selected}
      size="sm"
      subTitle={
        <>
          {showFixable && (
            <span
              onClick={(event) => {
                preventDefault(event)
                navigate(addFixableToLink(to))
              }}
              data-analytic-event={FIXABLE_DATA_ID_ANALYTIC_FUNNEL_CARD}
              className={cn(styles.fixable, isBlur && styles.hidden)}
            >{`${abbreviateNumber(fixable)} ${props.t("funnel.fixable")}`}</span>
          )}
          {showFixableV1 && (
            <span
              className={cn(
                styles.fixable,
                styles.fixableV1,
                isBlur && styles.hidden
              )}
            >
              <ToolOutlined />
              <span>{abbreviateNumber(fixable)}</span>
            </span>
          )}
        </>
      }
      {...rest}
    >
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${maxViewBox} ${maxViewBox}`}
        preserveAspectRatio="none"
      >
        {useGradient && (
          <linearGradient id={gradientId}>
            {color.map((gradientProps) => (
              <stop
                key={`${gradientId}${gradientProps.stopColor}`}
                {...gradientProps}
              />
            ))}
          </linearGradient>
        )}
        <path fill={fill} d={d} />
      </svg>
    </StatisticCard>
  )
}
